// src/components/GeneratedPurchaseOrder/index.jsx
import { Box, Grid, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import Header from './Header'
import styles from './index.module.css'
import ProductDetails from './PurchaseOrder'
import { Container } from '@mui/material'
import Footer from './Footer'
import { useProjectTemplateConfig } from '../../../hooks/useProjectTemplateConfig'
import { useParams, useLocation } from 'react-router-dom'
import {
  fetchPurchaseOrderDetails,
  updatePurchaseOrderDetails,
  readPurchaseOrderCache,
  writePurchaseOrderCache,
  invalidatePurchaseOrderCache,
  publishPurchaseOrderInvalidation,
  subscribePurchaseOrderInvalidation
} from '../../../services/purchaseOrder'
import PurchaseDetails from './PurchaseDetails'
import EstimationLoading from '../../EstimationLoader'
import { ToastContainer, toast } from 'react-toastify'
import Notes from './Notes'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { AuthContext } from '../../../helpers/AuthContext'
import {
  attachArtworkToDecorations,
  getArtworkDecorationId,
  getArtworkId,
  mergeApprovedProofIntoArtwork,
  mergeApprovedProofIntoDecorationArtwork,
  getArtworkPreviewSources,
  getArtworkRenderPreviewId,
  getDecorationId,
  getPurchaseOrderItemArtworks,
  resolvePurchaseOrderArtworks,
  getRenderPreviewSourceType,
  selectDecorationsForPurchaseOrder,
} from './proofPreviewHelpers'

// ───────────────────────────────────────────
//  B) utils
// ───────────────────────────────────────────
function parseAddressString(s) {
  if (!s) return null;
  const lines = String(s)
    .split('\n')
    .map(v => v.trim())
    .filter(Boolean);

  if (!lines.length) return null;

  const name = lines[0] || null;

  const cityIdx = lines.findIndex((ln, i) => i > 0 && ln.includes(','));
  const hasCityLine = cityIdx !== -1;

  const midStart = 1;
  const midEnd = hasCityLine ? cityIdx : lines.length; // exclusive
  const middle = lines.slice(midStart, midEnd);

  let company = null, street = null, street2 = null;
  if (middle.length >= 3) {
    [company, street, street2] = middle;
  } else if (middle.length === 2) {
    [company, street] = middle;
  } else if (middle.length === 1) {
    [street] = middle;
  }

  let city = null, state = null;
  if (hasCityLine) {
    const [c, st] = lines[cityIdx].split(',').map(v => v.trim());
    city = c || null;
    state = st || null;
  }

  let postal = null, country = null;
  const tail = hasCityLine ? lines.slice(cityIdx + 1) : [];
  if (tail.length === 1) {
    const parts = tail[0].split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      postal = parts.shift() || null;
      country = parts.join(' ') || null;
    } else {
      postal = parts[0] || null;
    }
  } else if (tail.length >= 2) {
    postal = tail[0] || null;
    country = tail.slice(1).join(' ') || null;
  }

  return {
    id: null,
    name: name || null,
    company: company || null,
    street: street || null,
    street2: street2 || null,
    city: city || null,
    state: state || null,
    postal: postal || null,
    country: country || null,
  };
}

// normalize server payload → UI shape
const normalizePO = (po) => {
  const approvedByDeco = (Array.isArray(po.proofs) ? po.proofs : [])
    .filter(p => p?.status === 'Approved' && p?.filePath && p?.decorationId)
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .reduce((acc, p) => { acc[String(p.decorationId)] = p; return acc }, {});

  const items = (po.items || []).map((item) => {
    const decorations = Array.isArray(item.decorations) ? [...item.decorations] : [];
    const baseArtworks = resolvePurchaseOrderArtworks(item, po.supplierInfo);
    const newArtworks = [...baseArtworks];

    decorations.forEach((dec, decIdx) => {
      const decId = String(dec?.id ?? dec?.Id ?? dec?.decorationId ?? dec?.DecorationId ?? '');
      const ap = decId ? approvedByDeco[decId] : null;
      if (!ap) return;
      while (newArtworks.length <= decIdx) newArtworks.push({});
      newArtworks[decIdx] = mergeApprovedProofIntoArtwork({
        artwork: newArtworks[decIdx],
        approvedProof: ap,
        proofLink: `${process.env.REACT_APP_FRONTEND_URL || process.env.FRONTEND_APP_URL}/generated-purchase-order/${po.id}`,
      });
      decorations[decIdx] = mergeApprovedProofIntoDecorationArtwork({
        decoration: dec,
        approvedProof: ap,
      });
    });

    return {
      ...item,
      decorations,
      ArtWorks: newArtworks,
      // Keep the pre-proof source for render-mode reconciliation only.
      __originalArtworkSources: baseArtworks,
      charges: Array.isArray(item.charges) ? item.charges : []
    };
  });

  const topLevelCharges =
    Array.isArray(po.charges) ? po.charges
      : Array.isArray(po?.supplierInfo?.charges) ? po.supplierInfo.charges
      : [];

  if ((!items || items.length === 0) && po.purchaseOrderType === 'Other_service_art') {
    items.push({
      Name: 'Art Services',
      ProductId: 'service-art',
      pricings: [],
      decorations: [],
      ArtWorks: [],
      charges: topLevelCharges
    });
  }

  const shipTo =
    po.shipTo ||
    po?.supplierInfo?.shipTo ||
    parseAddressString(po?.shippingAddress) ||
    null;

  const billTo =
    po.billTo ||
    po?.supplierInfo?.billTo ||
    parseAddressString(po?.billingAddress) ||
    (shipTo ? { ...shipTo, __tempSameAsShipping: true } : null);

  const shippingMethod =
    po.shippingMethod ||
    po?.supplierInfo?.shippingMethod ||
    po?.items?.[0]?.imprinting?.shipping?.method ||
    null;

  return {
    ...po,
    items,
    shipTo,
    billTo,
    shippingMethod,
    charges: topLevelCharges,
  };
};

const cleanNote = (note) => (typeof note === 'string' ? note.trim() : '');

const buildGeneratedPurchaseOrderPreviewRows = (purchaseOrderDetails = null) => {
  if (!purchaseOrderDetails || !Array.isArray(purchaseOrderDetails.items)) return [];

  return purchaseOrderDetails.items.flatMap((item) => {
    const orderItemId = item?.orderItemId ?? item?.OrderItemId ?? item?.id ?? null;
    const itemArtworks = resolvePurchaseOrderArtworks(item, purchaseOrderDetails.supplierInfo);
    const decorations = selectDecorationsForPurchaseOrder({
      purchaseOrderType: purchaseOrderDetails?.purchaseOrderType,
      supplierInfo: purchaseOrderDetails?.supplierInfo,
      item,
    });
    const joinedDecorations = attachArtworkToDecorations({
      decorations,
      artworks: itemArtworks,
    });

    if (joinedDecorations.length) {
      return joinedDecorations.map((decoration, index) => {
        const decorationId = getDecorationId(decoration);
        const sourceIndex = decoration?.__scopeIndex ?? index;
        const artwork =
          decoration?.ArtWork ||
          decoration?.artwork ||
          itemArtworks.find(
            (candidate) =>
              decorationId &&
              getArtworkDecorationId(candidate) === decorationId
          ) ||
          itemArtworks[sourceIndex] ||
          {};
        const { originalFileUrl, storedPreviewUrl, previewStatus, previewSourceType } =
          getArtworkPreviewSources(artwork);

        return {
          orderItemId,
          decorationId: decorationId || null,
          artworkId: getArtworkId(artwork) || null,
          renderPreviewId: getArtworkRenderPreviewId(artwork),
          previewImageUrlPresent: Boolean(storedPreviewUrl),
          previewStatus,
          previewSourceType: previewSourceType || getRenderPreviewSourceType(artwork),
          originalFileUrlPresent: Boolean(originalFileUrl),
        };
      });
    }

    return itemArtworks.map((artwork) => {
      const { originalFileUrl, storedPreviewUrl, previewStatus, previewSourceType } =
        getArtworkPreviewSources(artwork);

      return {
        orderItemId,
        decorationId: artwork?.DecorationId ?? artwork?.decorationId ?? null,
        artworkId: artwork?.id ?? artwork?.artworkId ?? artwork?.ArtWorkId ?? null,
        renderPreviewId: getArtworkRenderPreviewId(artwork),
        previewImageUrlPresent: Boolean(storedPreviewUrl),
        previewStatus,
        previewSourceType: previewSourceType || getRenderPreviewSourceType(artwork),
        originalFileUrlPresent: Boolean(originalFileUrl),
      };
    });
  });
};

const createInitialRenderState = (poId) => ({
  poId: poId || null,
  requestStarted: false,
  requestCompleted: false,
  responseStatus: null,
  dataLoaded: false,
  itemCount: 0,
  previewRowsBuilt: false,
  previewRows: [],
  error: null,
});

const GeneratedPurchaseOrder = () => {
  const { id } = useParams();
  const location = useLocation();

  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const [instanceUniqueId, setInstanceUniqueId] = useState(null);
  const [hasFreshRenderData, setHasFreshRenderData] = useState(false);
  const [previewRowsBuilt, setPreviewRowsBuilt] = useState(false);
  const isRenderMode = useMemo(
    () => new URLSearchParams(location.search).has('render'),
    [location.search]
  );
  const previewRows = useMemo(
    () => buildGeneratedPurchaseOrderPreviewRows(purchaseOrderDetails),
    [purchaseOrderDetails]
  );

  const { authState } = useContext(AuthContext);
  const currentUser = authState?.user || authState || {};
  const isAdministrator = useMemo(() => {
    const roleList = []
      .concat(currentUser?.roles || [])
      .concat(currentUser?.role ? [currentUser.role] : [])
      .map((role) => String(role).trim().toLowerCase().replace(/[_\s]+/g, '-'));
    return ['administrator', 'admin', 'super-administrator', 'superadmin'].some((role) => roleList.includes(role));
  }, [currentUser]);

  const { headerConfig, footerAddress, termsHtml } = useProjectTemplateConfig({
    instanceUniqueId,
    viewKey: 'purchaseOrder',
  });
  const purchaseOrderItems = purchaseOrderDetails?.items || [];
  const normalizedDecoratorNote = useMemo(
    () =>
      purchaseOrderItems
        .map((item) => cleanNote(item?.shipping?.decoratorNotes))
        .find(Boolean) || '',
    [purchaseOrderItems]
  );
  const normalizedSupplierNote = useMemo(
    () =>
      purchaseOrderItems
        .map((item) => cleanNote(item?.shipping?.supplierNotes))
        .find(Boolean) || '',
    [purchaseOrderItems]
  );
  const poType =
    purchaseOrderDetails?.purchaseOrderType ||
    purchaseOrderDetails?.supplierInfo?.type ||
    '';
  const isDecoratorPo =
    poType === 'Different_supplier_decoration' ||
    poType === 'Same_supplier_decoration' ||
    Boolean(purchaseOrderDetails?.supplierInfo?.decoratorId);
  const isSupplierPo =
    poType === 'No_decoration' ||
    !isDecoratorPo;
  let purchaseDetailsNote = '';
  let purchaseDetailsNoteType = null;
  const isPoRenderReady = Boolean(
    purchaseOrderDetails &&
    (!isRenderMode || hasFreshRenderData) &&
    previewRowsBuilt
  );

  if (isDecoratorPo && normalizedDecoratorNote) {
    purchaseDetailsNote = normalizedDecoratorNote;
    purchaseDetailsNoteType = 'decorator';
  } else if (isSupplierPo && normalizedSupplierNote) {
    purchaseDetailsNote = normalizedSupplierNote;
    purchaseDetailsNoteType = 'shipping';
  }

  // ───────────────────────────────────────────
  //  C) loader using your service + our cache
  // ───────────────────────────────────────────
  const getPurchaseOrderDetails = async ({ useOverlay = false } = {}) => {
    try {
      if (useOverlay) setOverlay(true);
      setLoading(true);
      setPreviewRowsBuilt(false);
      if (typeof window !== 'undefined') {
        window.__PO_RENDER_STATE__ = {
          ...createInitialRenderState(id),
          requestStarted: true,
        };
      }
      const response = await fetchPurchaseOrderDetails(id);
      const po = normalizePO(response?.purchaseOrder || {});
      setPurchaseOrderDetails(po);
      setHasFreshRenderData(true);
      if (typeof window !== 'undefined') {
        window.__PO_RENDER_STATE__ = {
          ...(window.__PO_RENDER_STATE__ || createInitialRenderState(id)),
          poId: id,
          requestStarted: true,
          requestCompleted: true,
          responseStatus: 200,
          dataLoaded: true,
          itemCount: Array.isArray(po?.items) ? po.items.length : 0,
          error: null,
        };
      }
      if (!isRenderMode) {
        writePurchaseOrderCache(id, po);
      }
    } catch (error) {
      console.error("Failed to fetch PO details:", error);
      if (typeof window !== 'undefined') {
        window.__PO_RENDER_STATE__ = {
          ...(window.__PO_RENDER_STATE__ || createInitialRenderState(id)),
          poId: id,
          requestStarted: true,
          requestCompleted: true,
          responseStatus: error?.response?.status ?? null,
          dataLoaded: false,
          error: {
            message: error.message,
            status: error?.response?.status ?? null,
          },
        };
      }
      toast.error('Failed to load purchase order');
    } finally {
      setLoading(false);
      setOverlay(false);
    }
  };

  // first paint: read cache; then revalidate (overlay only if no cache)
  useEffect(() => {
    if (!id) return;
    setHasFreshRenderData(false);
    if (isRenderMode) {
      invalidatePurchaseOrderCache(id);
      getPurchaseOrderDetails({ useOverlay: true });
      return () => {};
    }
    const cached = readPurchaseOrderCache(id);
    if (cached) {
      setPurchaseOrderDetails(cached);
      getPurchaseOrderDetails({ useOverlay: false });
    } else {
      getPurchaseOrderDetails({ useOverlay: true });
    }
    const off = subscribePurchaseOrderInvalidation(id, () => {
      invalidatePurchaseOrderCache(id);
      getPurchaseOrderDetails({ useOverlay: false });
    });
    return off;
  }, [id, isRenderMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__ELITE_CORE_API_BASE_URL__ =
      process.env.REACT_APP_NODEJS_SERVER_URL || "";
    window.__PO_RENDER_STATE__ = createInitialRenderState(id);
    window.__PO_GENERATED_PURCHASE_ORDER_PREVIEW_ROWS__ = [];
    return () => {
      window.__PO_RENDER_STATE__ = createInitialRenderState(id);
      window.__PO_GENERATED_PURCHASE_ORDER_PREVIEW_ROWS__ = [];
    };
  }, [id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!purchaseOrderDetails) {
      window.__PO_GENERATED_PURCHASE_ORDER_PREVIEW_ROWS__ = [];
      window.__PO_RENDER_STATE__ = {
        ...(window.__PO_RENDER_STATE__ || createInitialRenderState(id)),
        poId: id,
        previewRowsBuilt: false,
        previewRows: [],
      };
      setPreviewRowsBuilt(false);
      return;
    }

    window.__PO_GENERATED_PURCHASE_ORDER_PREVIEW_ROWS__ = previewRows;
    window.__PO_RENDER_STATE__ = {
      ...(window.__PO_RENDER_STATE__ || createInitialRenderState(id)),
      poId: id,
      previewRowsBuilt: true,
      previewRows,
    };
    setPreviewRowsBuilt(true);
  }, [id, previewRows, purchaseOrderDetails]);

  useEffect(() => {
    const orderId = purchaseOrderDetails?.orderId || purchaseOrderDetails?.OrderId;
    if (!orderId) return;
    let alive = true;
    const loadInstance = async () => {
      try {
        const url = `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-order/${orderId}`;
        const res = await axios.get(url);
        const order = res?.data?.order || {};
        const raw = order?.InstanceUniqueIds;
        let resolved = null;
        if (Array.isArray(raw)) {
          resolved = raw[0] || null;
        } else if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            resolved = Array.isArray(parsed) ? parsed[0] : parsed;
          } catch {
            resolved = raw;
          }
        }
        if (alive) setInstanceUniqueId(resolved);
      } catch (err) {
        if (alive) setInstanceUniqueId(null);
      }
    };
    loadInstance();
    return () => {
      alive = false;
    };
  }, [purchaseOrderDetails?.orderId, purchaseOrderDetails?.OrderId]);

  // ───────────────────────────────────────────
  //  Compact mode class toggling (universal)
  //  Adds body.pdf-compact when:
  //   - ?mode=compact
  //   - OR ?compact=1/true/yes
  //  Works on screen & PDF; updates on route changes.
  // ───────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const byMode = params.get("mode") === "compact";
    const byCompact = ["1", "true", "yes"].includes(String(params.get("compact") || "").toLowerCase());

    if (byMode || byCompact) {
      document.body.classList.add("pdf-compact");
    } else {
      document.body.classList.remove("pdf-compact");
    }

    // optional: keyboard toggle for quick QA (Ctrl/Cmd + P)
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        document.body.classList.toggle("pdf-compact");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [location.search]);

  // ───────────────────────────────────────────
  //  D) actions
  // ───────────────────────────────────────────
  const handleApprove = async ({ orderApproved }) => {
    try {
      setLoading(true);
      await updatePurchaseOrderDetails(id, {
        orderApproved: !!orderApproved,
        requestChange: false
      });

      invalidatePurchaseOrderCache(id);
      publishPurchaseOrderInvalidation(id);

      await getPurchaseOrderDetails({ useOverlay: false });
      toast.success('Purchase Order Approved');
    } catch (e) {
      console.error(e);
      toast.error('Approve failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestChange = async ({ firstName, lastName, email, message }) => {
    try {
      setLoading(true);
      await updatePurchaseOrderDetails(id, {
        requestChange: true,
        orderApproved: false,
        productionRepStatus: 'Problem',
        proofComments: message,
        nextAction: `Change requested by ${firstName} ${lastName} <${email}>`,
        nextActionDate: new Date().toISOString(),
      });
      invalidatePurchaseOrderCache(id);
      publishPurchaseOrderInvalidation(id);
      await getPurchaseOrderDetails({ useOverlay: false });
      toast.success('Change request sent');
    } catch (e) {
      console.error(e);
      toast.error('Request change failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer className={styles.toastContainer} />
      {overlay && <EstimationLoading />}

      <Box className={styles.rootContainer}>
        <Container className={styles.printDocument} data-po-print-document>
          <Header
            poNumber={purchaseOrderDetails?.poNumber}
            poDate={purchaseOrderDetails?.createdAt}
            supplierName={purchaseOrderDetails?.supplierInfo?.supplierName}
            isLoaded={!!purchaseOrderDetails}
            headerConfig={headerConfig}
          />

          <PurchaseDetails
            purchaseOrderDetails={purchaseOrderDetails}
            Description={purchaseOrderDetails?.notes}
            note={purchaseDetailsNote}
            noteType={purchaseDetailsNoteType}
          />

          {isPoRenderReady && (
            <span id="po-ready" style={{ display: 'none' }} />
          )}

          {purchaseOrderDetails?.notes && <Notes notes={purchaseOrderDetails.notes} />}

          <Box className={styles.PresentationContainer}>
            <Grid container spacing={2} className={styles.presentationCard}>
              {purchaseOrderDetails?.items?.map((data, index) => {
                const orderItemId = data?.orderItemId ?? data?.OrderItemId ?? data?.id;
                const lineId = data?.cartLineId ?? data?.lineId;
                const itemKey = orderItemId ?? lineId ?? `legacy-${index}`;
                return (
                  <Grid item xs={12} key={`po-order-item-${itemKey}`} className={styles.presentationGrid}>
                    <ProductDetails
                      ls
                      productData={data}
                      currency={purchaseOrderDetails?.currency}
                      purchaseOrderId={purchaseOrderDetails?.purchaseOrderId}
                      type={purchaseOrderDetails?.purchaseOrderType}
                      supplierInfo={purchaseOrderDetails?.supplierInfo}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          <Footer
            purchaseOrderDetails={purchaseOrderDetails}
            handleApprove={handleApprove}
            onRequestChange={handleRequestChange}
            busy={loading}
            footerAddress={footerAddress}
            termsHtml={termsHtml}
          />


          {isAdministrator && (
            <Box className={styles.debugControls} sx={{ mt: 2, mb: 2 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Debug: Generated Purchase Order Payload</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}
                  >
                    {JSON.stringify(purchaseOrderDetails, null, 2)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default GeneratedPurchaseOrder;
